import useToastContext from "hooks/useToastContext";
import apiPath from "../../utils/apiPath";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import dayjs from "dayjs";
import { isEmpty } from "lodash";
import { useEffect, useState, useCallback } from "react";
import { apiPost } from "../../utils/apiFetch";
import helpers from "utils/helpers";
import { numericInputPattern, preventMaxInput } from "utils/validations";

const inputBase = "w-full px-3 py-2 text-sm text-gray-900 bg-white border rounded-lg focus:outline-none focus:ring-1";
const inputOk = "border-[#DFDFDF] focus:border-blue-500 focus:ring-blue-500";
const inputErr = "border-red-500 focus:border-red-500 focus:ring-red-500";

const GenerateSalaryList = ({ generateList, setGenerateList, allGenerateSalary, selectedDepartment, startDate, endDate, duration }) => {
  const notification = useToastContext();
  const { t } = useTranslation();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    shouldFocusError: true,
    defaultValues: {},
  });

  const [list, setList] = useState([]);

  const onSubmit = async (data) => {
    try {
      const numberOfObjects = generateList?.length || 0;
      const results = [];
      for (let i = 0; i < numberOfObjects; i++) {
        const newObj = {
          notes: data.notes?.[i] || "",
          otherAllowance: Number(data.otherAllowanceOne?.[i] || 0), // Defaults to 0
          otherDeduction: Number(data.otherAllowanceTwo?.[i] || 0), // Defaults to 0
          salaryId: data?.salaryId?.[i],
          employeeId: data?.employeeId?.[i],
          fromDate: startDate,
          toDate: endDate,
        };
        results.push(newObj);
      }
      const payload = {
        employee: results,
        duration: duration,
      };
      const path = apiPath.generateSalary;
      const result = await apiPost(path, payload);

      function successCallback() {
        allGenerateSalary();
        notification.success(result?.data.message);
      }
      function errorCallback() {
        notification.error(result?.data.message);
      }

      helpers.handleResult(result.data.success, successCallback, errorCallback);
    } catch (error) {
      console.log("error in get all generate list==>>>>", error);
      notification.error(error.message);
    }
  };

  const calculationForAllowance = useCallback(
    (item, i) => {
      const currentValues = watch();
      const allowanceOne = currentValues?.otherAllowanceOne || [];
      const deductionOne = currentValues?.otherAllowanceTwo || [];

      const changeAllowance = Number(allowanceOne[i] || 0);
      const changeDeduction = Number(deductionOne[i] || 0);

      const totalAllowance = Number(item?.totalAllowance || 0) + changeAllowance;
      const totalDeduction = Number(item?.totalDeduction || 0) + changeDeduction;

      setList((prev) => {
        const newList = [...prev];
        if (!newList[i]) {
          newList[i] = {};
        }
        newList[i].newAllowance = totalAllowance;
        newList[i].newDeduction = totalDeduction;
        return newList;
      });
    },
    [watch]
  );

  useEffect(() => {
    reset();
    if (generateList?.length > 0) {
      const allList = generateList.map((data) => ({
        newDeduction: data?.totalDeduction || 0,
        newAllowance: data?.totalAllowance || 0,
      }));
      setList(allList);

      generateList.forEach((item, i) => {
        setValue(`salaryId[${i}]`, item?._id);
        setValue(`employeeId[${i}]`, item?.employeeID);
      });
    } else {
      setList([]);
    }
  }, [generateList, reset, setValue]);

  const renderNoRecordRow = () => (
    <tr className="bg-white border-b text-center dark:bg-gray-800 dark:border-gray-700">
      <td className="py-4 px-3 border-r" colSpan={12}>
        {t("O_NO_RECORD_FOUND")}
      </td>
    </tr>
  );

  const generateButton = !isEmpty(generateList) ? (
    <button
      className="m-4 bg-LightBlue text-white active:bg-emerald-600 font-normal text-sm px-8 py-2.5 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 ease-linear transition-all duration-150"
      type="submit"
    >
      Generate
    </button>
  ) : null;

  // FIX: Removed 'required' and 'min'. Only checks for max length and digits.
  const amountRules = (item, i, extra = {}) => ({
    maxLength: { value: 10, message: "Maximum limit is 10 characters." },
    pattern: { value: /^\d*$/, message: "Only digits are allowed." }, // ^\d*$ allows empty string while typing
    onChange: () => calculationForAllowance(item, i),
    ...(extra.validate ? { validate: extra.validate } : {}),
  });

  return (
    <>
      {selectedDepartment?.length > 0 && (
        <div>
          <div className="p-3">
            <div className="overflow-x-auto relative rounded-lg border">
              <form onSubmit={handleSubmit(onSubmit)} method="post">
                <table className="w-full text-xs text-left text-[#6c6c6c] dark:text-gray-400">
                  <thead className="text-xs text-gray-900 border border-[#E1E6EE] bg-[#E1E6EE] dark:bg-gray-700 dark:text-gray-400">
                    <tr>
                      <th scope="col" className="py-3 px-3">Employee ID</th>
                      <th scope="col" className="py-3 px-3">Employee Name</th>
                      <th scope="col" className="py-3 px-3">Department</th>
                      <th scope="col" className="py-3 px-3">Duration Type</th>
                      <th scope="col" className="py-3 px-3">From Date</th>
                      <th scope="col" className="py-3 px-3">To Date</th>
                      <th scope="col" className="py-3 px-3 text-center w-[300px] min-w-[260px]">Others</th>
                    </tr>
                  </thead>
                  <tbody>
                    {generateList?.length > 0
                      ? generateList.map((item, i) => (
                          <tr key={item?._id || i} className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 align-top">
                            <td className="py-4 px-3 border-r text-gray-900">{item?.Employee?.employeeId}</td>
                            <td className="py-4 px-3 border-r text-gray-900">{item?.Employee?.name}</td>
                            <td className="py-4 px-3 border-r text-gray-900">{item?.Department?.name}</td>
                            <td className="py-4 px-3 border-r text-gray-900">{item?.JobRequests?.duration}</td>
                            <td className="py-4 px-3 border-r text-gray-900">{startDate ? dayjs(startDate).format("MM-DD-YYYY") : ""}</td>
                            <td className="py-4 px-3 border-r text-gray-900">{endDate ? dayjs(endDate).format("MM-DD-YYYY") : ""}</td>
                            <td className="py-4 px-3 border-r text-gray-900">
                              
                              {/* Existing allowances / deductions */}
                              {(item?.allowance?.length > 0 || item?.deduction?.length > 0) && (
                                <div className="flex flex-wrap gap-3 mb-4">
                                  {item?.allowance?.map((allowance, index) => (
                                    <div key={`a${index}`}>
                                      <div className="text-xs font-bold text-gray-900 mb-1">Allowance {index + 1}</div>
                                      <div className="min-w-[70px] px-3 py-2 text-sm font-normal border border-[#E3E3E3] rounded-lg bg-gray-50">
                                        {allowance?.allowanceChild}
                                      </div>
                                    </div>
                                  ))}
                                  {item?.deduction?.map((deduction, index) => (
                                    <div key={`d${index}`}>
                                      <div className="text-xs font-bold text-gray-900 mb-1">Deduction {index + 1}</div>
                                      <div className="min-w-[70px] px-3 py-2 text-sm font-normal border border-[#E3E3E3] rounded-lg bg-gray-50">
                                        {deduction?.deductionChild}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Other allowance / deduction inputs */}
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  {/* FIX: Removed red asterisk */}
                                  <label htmlFor={`otherAllowanceOne${i}`} className="block text-xs font-bold text-gray-900 mb-1">
                                    Other Allowance
                                  </label>
                                  <input
                                    type="text"
                                    id={`otherAllowanceOne${i}`}
                                    maxLength={10}
                                    placeholder="0"
                                    autoComplete="off"
                                    onInput={(e) => preventMaxInput(e, 10)}
                                    onKeyDown={numericInputPattern}
                                    className={`${inputBase} ${errors?.otherAllowanceOne?.[i] ? inputErr : inputOk}`}
                                    {...register(`otherAllowanceOne[${i}]`, amountRules(item, i))}
                                  />
                                  {errors?.otherAllowanceOne?.[i]?.message && (
                                    <p className="mt-1 text-xs text-red-500">{errors.otherAllowanceOne[i].message}</p>
                                  )}
                                </div>

                                <div>
                                  {/* FIX: Removed red asterisk */}
                                  <label htmlFor={`otherAllowanceTwo${i}`} className="block text-xs font-bold text-gray-900 mb-1">
                                    Other Deduction
                                  </label>
                                  <input
                                    type="text"
                                    id={`otherAllowanceTwo${i}`}
                                    maxLength={10}
                                    placeholder="0"
                                    autoComplete="off"
                                    onInput={(e) => preventMaxInput(e, 10)}
                                    onKeyDown={numericInputPattern}
                                    className={`${inputBase} ${errors?.otherAllowanceTwo?.[i] ? inputErr : inputOk}`}
                                    {...register(
                                      `otherAllowanceTwo[${i}]`,
                                      amountRules(item, i, {
                                        validate: (value) => {
                                          if (!value) return null;
                                          if (!list[i] || list[i].newAllowance === undefined) return null;
                                          const maxDeduction = list[i].newAllowance - (item?.totalDeduction ?? 0);
                                          return +value > maxDeduction ? `Deduction must be less than ${maxDeduction}.` : null;
                                        },
                                      })
                                    )}
                                  />
                                  {errors?.otherAllowanceTwo?.[i]?.message && (
                                    <p className="mt-1 text-xs text-red-500">{errors.otherAllowanceTwo[i].message}</p>
                                  )}
                                </div>
                              </div>

                              {/* Notes */}
                              <div className="mt-4">
                                <label htmlFor={`notes${i}`} className="block text-xs font-bold text-gray-900 mb-1">
                                  Notes
                                </label>
                                <textarea
                                  id={`notes${i}`}
                                  rows={2}
                                  className={`${inputBase} ${inputOk} resize-y`}
                                  placeholder="Add a note (optional)"
                                  {...register(`notes[${i}]`)}
                                />
                              </div>

                              {/* Net pay */}
                              <div className="flex items-center justify-between mt-4 px-3 py-2 rounded-lg bg-gray-50 border border-[#E3E3E3]">
                                <strong className="text-sm text-gray-900">Net Pay</strong>
                                <p className="text-base font-semibold text-gray-900">
                                  ${(list[i]?.newAllowance || item?.totalAllowance || 0) - (list[i]?.newDeduction || item?.totalDeduction || 0)}
                                </p>
                              </div>
                            </td>
                          </tr>
                        ))
                      : renderNoRecordRow()}
                  </tbody>
                </table>
                {generateButton}
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GenerateSalaryList;





// import useToastContext from "hooks/useToastContext";
// import apiPath from "../../utils/apiPath";
// import { useTranslation } from "react-i18next";
// import { useForm } from "react-hook-form";
// import dayjs from "dayjs";
// import { isEmpty } from "lodash";
// import { useEffect, useState, useCallback } from "react";
// import { apiPost } from "../../utils/apiFetch";
// import helpers from "utils/helpers";
// import { numericInputPattern, preventMaxInput } from "utils/validations";

// const inputBase = "w-full px-3 py-2 text-sm text-gray-900 bg-white border rounded-lg focus:outline-none focus:ring-1";
// const inputOk = "border-[#DFDFDF] focus:border-blue-500 focus:ring-blue-500";
// const inputErr = "border-red-500 focus:border-red-500 focus:ring-red-500";

// const GenerateSalaryList = ({ generateList, setGenerateList, allGenerateSalary, selectedDepartment, startDate, endDate, duration }) => {
//   const notification = useToastContext();
//   const { t } = useTranslation();

//   const {
//     register,
//     handleSubmit,
//     watch,
//     setValue,
//     reset,
//     formState: { errors },
//   } = useForm({
//     mode: "onChange",
//     shouldFocusError: true,
//     defaultValues: {},
//   });

//   const [list, setList] = useState([]);

//   const onSubmit = async (data) => {
//     try {
//       const numberOfObjects = data?.otherAllowanceOne?.length || 0;
//       const results = [];
//       for (let i = 0; i < numberOfObjects; i++) {
//         const newObj = {
//           notes: data.notes?.[i] || "",
//           otherAllowance: Number(data.otherAllowanceOne[i] || 0),
//           otherDeduction: Number(data.otherAllowanceTwo[i] || 0),
//           salaryId: data?.salaryId[i],
//           employeeId: data?.employeeId[i],
//           fromDate: startDate,
//           toDate: endDate,
//         };
//         results.push(newObj);
//       }
//       const payload = {
//         employee: results,
//         duration: duration,
//       };
//       const path = apiPath.generateSalary;
//       const result = await apiPost(path, payload);

//       function successCallback() {
//         allGenerateSalary();
//         notification.success(result?.data.message);
//       }
//       function errorCallback() {
//         notification.error(result?.data.message);
//       }

//       helpers.handleResult(result.data.success, successCallback, errorCallback);
//     } catch (error) {
//       console.log("error in get all generate list==>>>>", error);
//       notification.error(error.message);
//     }
//   };

//   const calculationForAllowance = useCallback(
//     (item, i) => {
//       const currentValues = watch();
//       const allowanceOne = currentValues?.otherAllowanceOne || [];
//       const deductionOne = currentValues?.otherAllowanceTwo || [];

//       const changeAllowance = Number(allowanceOne[i] || 0);
//       const changeDeduction = Number(deductionOne[i] || 0);

//       const totalAllowance = Number(item?.totalAllowance || 0) + changeAllowance;
//       const totalDeduction = Number(item?.totalDeduction || 0) + changeDeduction;

//       setList((prev) => {
//         const newList = [...prev];
//         if (!newList[i]) {
//           newList[i] = {};
//         }
//         newList[i].newAllowance = totalAllowance;
//         newList[i].newDeduction = totalDeduction;
//         return newList;
//       });
//     },
//     [watch]
//   );

//   useEffect(() => {
//     reset();
//     if (generateList?.length > 0) {
//       const allList = generateList.map((data) => ({
//         newDeduction: data?.totalDeduction || 0,
//         newAllowance: data?.totalAllowance || 0,
//       }));
//       setList(allList);

//       generateList.forEach((item, i) => {
//         setValue(`salaryId[${i}]`, item?._id);
//         setValue(`employeeId[${i}]`, item?.employeeID);
//       });
//     } else {
//       setList([]);
//     }
//   }, [generateList, reset, setValue]);

//   const renderNoRecordRow = () => (
//     <tr className="bg-white border-b text-center dark:bg-gray-800 dark:border-gray-700">
//       <td className="py-4 px-3 border-r" colSpan={12}>
//         {t("O_NO_RECORD_FOUND")}
//       </td>
//     </tr>
//   );

//   const generateButton = !isEmpty(generateList) ? (
//     <button
//       className="m-4 bg-LightBlue text-white active:bg-emerald-600 font-normal text-sm px-8 py-2.5 rounded shadow hover:shadow-lg outline-none focus:outline-none mr-1 ease-linear transition-all duration-150"
//       type="submit"
//     >
//       Generate
//     </button>
//   ) : null;

//   const amountRules = (item, i, extra = {}) => ({
//     required: { value: true, message: extra.requiredMessage },
//     maxLength: { value: 10, message: "Maximum limit is 10 characters." },
//     pattern: { value: /^\d+$/, message: "Only digits are allowed." },
//     min: { value: 1, message: "Minimum value must be 1." },
//     onChange: () => calculationForAllowance(item, i),
//     ...(extra.validate ? { validate: extra.validate } : {}),
//   });

//   return (
//     <>
//       {selectedDepartment?.length > 0 && (
//         <div>
//           <div className="p-3">
//             <div className="overflow-x-auto relative rounded-lg border">
//               <form onSubmit={handleSubmit(onSubmit)} method="post">
//                 <table className="w-full text-xs text-left text-[#6c6c6c] dark:text-gray-400">
//                   <thead className="text-xs text-gray-900 border border-[#E1E6EE] bg-[#E1E6EE] dark:bg-gray-700 dark:text-gray-400">
//                     <tr>
//                       <th scope="col" className="py-3 px-3">Employee ID</th>
//                       <th scope="col" className="py-3 px-3">Employee Name</th>
//                       <th scope="col" className="py-3 px-3">Department</th>
//                       <th scope="col" className="py-3 px-3">Duration Type</th>
//                       <th scope="col" className="py-3 px-3">From Date</th>
//                       <th scope="col" className="py-3 px-3">To Date</th>
//                       <th scope="col" className="py-3 px-3 text-center w-[300px] min-w-[260px]">Others</th>
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {generateList?.length > 0
//                       ? generateList.map((item, i) => (
//                           <tr key={item?._id || i} className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 align-top">
//                             <td className="py-4 px-3 border-r text-gray-900">{item?.Employee?.employeeId}</td>
//                             <td className="py-4 px-3 border-r text-gray-900">{item?.Employee?.name}</td>
//                             <td className="py-4 px-3 border-r text-gray-900">{item?.Department?.name}</td>
//                             <td className="py-4 px-3 border-r text-gray-900">{item?.JobRequests?.duration}</td>
//                             <td className="py-4 px-3 border-r text-gray-900">{startDate ? dayjs(startDate).format("MM-DD-YYYY") : ""}</td>
//                             <td className="py-4 px-3 border-r text-gray-900">{endDate ? dayjs(endDate).format("MM-DD-YYYY") : ""}</td>
//                             <td className="py-4 px-3 border-r text-gray-900">
//                               {/* Existing allowances / deductions (read only) */}
//                               {(item?.allowance?.length > 0 || item?.deduction?.length > 0) && (
//                                 <div className="flex flex-wrap gap-3 mb-4">
//                                   {item?.allowance?.map((allowance, index) => (
//                                     <div key={`a${index}`}>
//                                       <div className="text-xs font-bold text-gray-900 mb-1">Allowance {index + 1}</div>
//                                       <div className="min-w-[70px] px-3 py-2 text-sm font-normal border border-[#E3E3E3] rounded-lg bg-gray-50">
//                                         {allowance?.allowanceChild}
//                                       </div>
//                                     </div>
//                                   ))}
//                                   {item?.deduction?.map((deduction, index) => (
//                                     <div key={`d${index}`}>
//                                       <div className="text-xs font-bold text-gray-900 mb-1">Deduction {index + 1}</div>
//                                       <div className="min-w-[70px] px-3 py-2 text-sm font-normal border border-[#E3E3E3] rounded-lg bg-gray-50">
//                                         {deduction?.deductionChild}
//                                       </div>
//                                     </div>
//                                   ))}
//                                 </div>
//                               )}

//                               {/* Other allowance / deduction inputs */}
//                               <div className="grid grid-cols-2 gap-3">
//                                 <div>
//                                   <label htmlFor={`otherAllowanceOne${i}`} className="block text-xs font-bold text-gray-900 mb-1">
//                                     Other Allowance <span className="text-red-500">*</span>
//                                   </label>
//                                   <input
//                                     type="text"
//                                     id={`otherAllowanceOne${i}`}
//                                     maxLength={10}
//                                     placeholder="0"
//                                     autoComplete="off"
//                                     onInput={(e) => preventMaxInput(e, 10)}
//                                     onKeyDown={numericInputPattern}
//                                     className={`${inputBase} ${errors?.otherAllowanceOne?.[i] ? inputErr : inputOk}`}
//                                     {...register(`otherAllowanceOne[${i}]`, amountRules(item, i, { requiredMessage: "Please enter other allowance." }))}
//                                   />
//                                   {errors?.otherAllowanceOne?.[i]?.message && (
//                                     <p className="mt-1 text-xs text-red-500">{errors.otherAllowanceOne[i].message}</p>
//                                   )}
//                                 </div>

//                                 <div>
//                                   <label htmlFor={`otherAllowanceTwo${i}`} className="block text-xs font-bold text-gray-900 mb-1">
//                                     Other Deduction <span className="text-red-500">*</span>
//                                   </label>
//                                   <input
//                                     type="text"
//                                     id={`otherAllowanceTwo${i}`}
//                                     maxLength={10}
//                                     placeholder="0"
//                                     autoComplete="off"
//                                     onInput={(e) => preventMaxInput(e, 10)}
//                                     onKeyDown={numericInputPattern}
//                                     className={`${inputBase} ${errors?.otherAllowanceTwo?.[i] ? inputErr : inputOk}`}
//                                     {...register(
//                                       `otherAllowanceTwo[${i}]`,
//                                       amountRules(item, i, {
//                                         requiredMessage: "Please enter other deduction.",
//                                         validate: (value) => {
//                                           if (!list[i] || list[i].newAllowance === undefined) return null;
//                                           const maxDeduction = list[i].newAllowance - (item?.totalDeduction ?? 0);
//                                           return +value > maxDeduction ? `Deduction must be less than ${maxDeduction}.` : null;
//                                         },
//                                       })
//                                     )}
//                                   />
//                                   {errors?.otherAllowanceTwo?.[i]?.message && (
//                                     <p className="mt-1 text-xs text-red-500">{errors.otherAllowanceTwo[i].message}</p>
//                                   )}
//                                 </div>
//                               </div>

//                               {/* Notes */}
//                               <div className="mt-4">
//                                 <label htmlFor={`notes${i}`} className="block text-xs font-bold text-gray-900 mb-1">
//                                   Notes
//                                 </label>
//                                 <textarea
//                                   id={`notes${i}`}
//                                   rows={2}
//                                   className={`${inputBase} ${inputOk} resize-y`}
//                                   placeholder="Add a note (optional)"
//                                   {...register(`notes[${i}]`)}
//                                 />
//                               </div>

//                               {/* Net pay */}
//                               <div className="flex items-center justify-between mt-4 px-3 py-2 rounded-lg bg-gray-50 border border-[#E3E3E3]">
//                                 <strong className="text-sm text-gray-900">Net Pay</strong>
//                                 <p className="text-base font-semibold text-gray-900">
//                                   ${(list[i]?.newAllowance || item?.totalAllowance || 0) - (list[i]?.newDeduction || item?.totalDeduction || 0)}
//                                 </p>
//                               </div>
//                             </td>
//                           </tr>
//                         ))
//                       : renderNoRecordRow()}
//                   </tbody>
//                 </table>
//                 {generateButton}
//               </form>
//             </div>
//           </div>
//         </div>
//       )}
//     </>
//   );
// };

// export default GenerateSalaryList;